import { useEffect } from 'react';

export const useVersionChecker = (currentBuildInfo) => {
  useEffect(() => {
    // 1. Log initialization
    //console.log('[VersionCheck] Hook initialized. Current (Local) Build Info:', currentBuildInfo);

    // Skip in development to prevent constant reloading if you aren't generating buildInfo locally
    if (process.env.NODE_ENV === 'development' && !currentBuildInfo) {
      console.log('[VersionCheck] Skipping check (Development mode & no buildInfo provided).');
      return;
    }

    const checkVersion = async () => {
      try {
        // 2. Log before fetching
        const url = `/version.json?t=${Date.now()}`;
        // console.log(`[VersionCheck] Fetching remote version info from: ${url}`);

        const response = await fetch(url, {
          cache: 'no-store',
          headers: {
            'Pragma': 'no-cache',
            'Cache-Control': 'no-cache'
          }
        });

        if (!response.ok) {
          console.warn(`[VersionCheck] Failed to fetch version.json. HTTP Status: ${response.status}`);
          return;
        }

        // 3. SAFETY CHECK: Ensure we actually got JSON back
        // (In local dev, missing files often return index.html with status 200)
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            console.warn('[VersionCheck] Response was not JSON (likely HTML fallback). Skipping version check.');
            return;
        }

        const serverVersion = await response.json();
        
        // 4. Log what we got back from the server
        // console.log('[VersionCheck] Remote (Server) Version Info:', serverVersion);

        // 5. Perform Comparison
        const clientCommit = currentBuildInfo?.commit;
        const serverCommit = serverVersion?.commit;

        // console.log(`[VersionCheck] Comparing Commits:\n   Local:  "${clientCommit}"\n   Remote: "${serverCommit}"`);

        // Logic: If server has a commit, and local is DIFFERENT (or missing), force refresh.
        if (serverCommit) {
            if (!clientCommit || clientCommit !== serverCommit) {
                console.warn(`[VersionCheck] ⚠️ VERSION MISMATCH DETECTED! (Local: ${clientCommit} vs Remote: ${serverCommit})`);
                console.warn(`[VersionCheck] Initiating hard refresh sequence...`);
                
                // Unregister Service Workers
                if ('serviceWorker' in navigator) {
                    try {
                        const registrations = await navigator.serviceWorker.getRegistrations();
                        for (const registration of registrations) {
                            await registration.unregister();
                            console.log('[VersionCheck] Unregistered SW:', registration);
                        }
                    } catch (swErr) {
                        console.error('[VersionCheck] Error unregistering SW:', swErr);
                    }
                }

                // Clear Caches
                if ('caches' in window) {
                    console.log('[VersionCheck] Clearing Cache Storage...');
                    try {
                        const keys = await caches.keys();
                        await Promise.all(keys.map(key => {
                            console.log(`[VersionCheck] Deleting cache key: ${key}`);
                            return caches.delete(key);
                        }));
                    } catch (err) {
                        console.error("[VersionCheck] Failed to clear caches:", err);
                    }
                }

                // Force Reload
                console.warn('[VersionCheck] Reloading window now...');
                window.location.reload(true);
                return;
            }
        }
        
        console.log(`[VersionCheck] ✅ Client is up to date.`);

      } catch (error) {
        console.error('[VersionCheck] Critical error during version check:', error);
      }
    };

    // Run immediately on mount
    checkVersion();

    // Check again every hour
    const interval = setInterval(() => {
        console.log('[VersionCheck] Running scheduled interval check...');
        checkVersion();
    }, 60 * 60 * 1000);

    return () => clearInterval(interval);

  }, [currentBuildInfo]);
};